import * as React from 'react';
import { connect } from 'react-redux';
import { AjaxError } from 'rxjs/ajax';
import SubmissionsService from '../../../Services/SubmissionsService';
import { ILiveCell } from "../../../../DataClasses/HdfData"
import { IGenericUserProps, genericMapStateToProps } from "../../../../DataClasses/GenericUser";
import { Theme, createStyles, createTheme, ThemeProvider } from '@mui/material/styles';;
import { withStyles, WithStyles } from '@mui/styles';;
import idssStyle from "../../../../Configuration/SharedStyling";
import { Box, Tab} from '@mui/material';
import { Redirect } from "react-router-dom";
import equal from 'fast-deep-equal'
import { TabContext, TabList, TabPanel } from '@mui/lab';
import SharedMaterialReactTable, { ISharedTableColumn } from "../../../Shared/MaterialReactTable";
import LoadingDataTable from '../../../Shared/LoadingDataTable';
import { firstValueFrom } from 'rxjs';

const styles = (theme: Theme) => createStyles({
    ...idssStyle(theme),
    liveMeasureTable: {
        borderSpacing: 0,
        margin: theme.spacing(2),
        borderBottom: "none",
        border: '1px solid #4f758b',
        "& tbody td": {
            padding: theme.spacing(2),
            border: '1px solid #4f758b',
            margin: '0px'
        }
    },
    rotate_text:
    {
        'moz-transform': 'rotate(-90deg)',
        'moz-transform - origin': 'top left',
        'webkit-transform': 'rotate(-90deg)',
        'webkit-transform-origin': 'top left',
        'o-transform': 'rotate(-90deg)',
        'o-transform-origin': 'top left',
        position: 'relative',
        top: '20px',
    },
    tabs: {
        "& .Mui-selected": {
            color: '#757575 !important',
            fontWeight: 'bold'
        }
    }
});

interface ILocalProps {
    measureCode: string,
    measureName: string,
    submissionId: string,
    productLine: string,
    measurementYear: number
}

interface ILocalState {
    measureData: Array<Array<ILiveCell>>;
    isLoading: boolean;
    redirect: boolean;
    isRaceEthnicity: boolean;
    curTabValue: string;

    measureBaseData: Array<Array<ILiveCell>>;
    measureRaceData: Array<Array<ILiveCell>>;
    measureEthnicityData: Array<Array<ILiveCell>>;
}

type IStylesProps = WithStyles<typeof styles>;

class LiveMeasureViewer extends React.Component<IGenericUserProps & ILocalProps & IStylesProps, ILocalState> {

    constructor(props: IGenericUserProps & IStylesProps & ILocalProps) {
        super(props);

        this.state = {
            isLoading: true,
            measureData: [],
            redirect: false,
            isRaceEthnicity: false,
            curTabValue: "1",

            measureBaseData: [],
            measureRaceData: [],
            measureEthnicityData: []
        }
    }

    public async componentDidMount() {
        await this.RefreshModel();
    }

    public async componentDidUpdate(prevProps) {
        if (!equal(this.props.measureCode, prevProps.measureCode)) {
            await this.RefreshModel();
        }
    }

    private redirectToErrorPage() {
        return <Redirect to='/errorpage' />;
    }

    private setRedirectState() {
        this.setState({ redirect: true });
    }

    getMuiTheme = () => createTheme({
        components: {
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        fontSize: '0.8125rem',
                        fontFamily: 'sans-serif',
                        border: '1px solid #E0E0E0',
                    },
                    head: {
                        color: "#4F758B",
                        fontWeight: 'bold'
                    },
                    paddingNone: {
                        paddingLeft: '2px',
                        paddingRight: '2px'
                    }
                }
            }
        }
    })

    // Custom cell renderer for Shared DataTable
    private renderCustomCell = (rowData: any, columnKey: string) => {
        const { classes } = this.props;
        const cellValue = rowData[columnKey];

        if (!cellValue || typeof cellValue !== 'object') {
            return cellValue;
        }

        // Handle the ILiveCell object
        const cell = cellValue as ILiveCell;

        // Determine cell styling based on foreground color
        let cellClass = "";
        let cellStyle: React.CSSProperties = {
            padding: '2px',
            fontWeight: cell.isBold ? 'bold' : 'normal'
        };

        if (cell.text !== "" && (cell.foregroundColor !== 'White' && cell.foregroundColor !== 'LightSteelBlue')) {
            if (cell.foregroundColor !== '#eeaf30') {
                cellClass = classes.tableCellNcqaBlue;
            } else {
                cellClass = classes.tableCellYellow;
            }
            cellStyle.width = "80px";
        } else {
            cellStyle.width = "max-content";
        }

        return (
            <div className={cellClass} style={cellStyle}>
                {cell.text}
            </div>
        );
    };

    // Convert the 2D array data to objects for SharedMaterialReactTable
    private convertDataForSharedMaterialTable = (data: Array<Array<ILiveCell>>) => {
        if (!data || data.length === 0) return [];

        const headers = data[0];
        const rows = data.slice(1);

        return rows.map(row => {
            const rowObj: any = {};
            headers.forEach((header, index) => {
                rowObj[this.getAccessorKey(header, index)] = row[index];
            });
            return rowObj;
        });
    };

    private getAccessorKey = (header: ILiveCell, index: number): string => {
        return `${header.text}_${index}`;
    }

    // Get table columns definition for SharedMaterialReactTable
    private getTableColumns = (headers: ILiveCell[]): ISharedTableColumn<any>[] => {
        return headers.map((header, index) => ({
            accessorKey: this.getAccessorKey(header, index),
            header: header.shortDisplayText.replace(/([a-z])([A-Z])/g, '$1 $2').trim(),
            enableSorting: false,
            enableFiltering: false,
            Cell: ({ row }) => this.renderCustomCell(row.original, this.getAccessorKey(header, index)),
        }));
    };

    public render() {
        if (this.state.redirect) {
            return this.redirectToErrorPage();
        }

        if (this.state.isLoading) {
            return (
                <div>
                    <LoadingDataTable titleText={"*"}/>
                </div>
            )
        }
        else {
            const { classes } = this.props;

            if (!this.state.measureData || this.state.measureData.length === 0) {
                return <div>No data available</div>;
            }

            const headers = this.state.measureData[0];
            const convertedData = this.convertDataForSharedMaterialTable(this.state.measureData);

            const dataTbl = (
                        <SharedMaterialReactTable
                            data={convertedData}
                            columns={this.getTableColumns(headers)}
                            title={`${this.props.measureName} (${this.props.measureCode})`}
                            isLoading={false}
                            enablePagination={true}
                            enableSorting={false}
                            enableFilterModal={false}
                            enableFiltering={false}
                            enableColumnActions={false}
                            enableRowActions={false}
                            enableGlobalSearch={false}
                            layoutMode="fixed"
                            initialState={{
                                pagination: { pageIndex: 0, pageSize: 100 },
                            }}
                            muiPaginationProps={{
                                rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                            }}
                            emptyMessage="No data available."
                        />);

            return (this.state.isRaceEthnicity ? <ThemeProvider theme={this.getMuiTheme()}><TabContext value={this.state.curTabValue}>
                <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
                    <TabList onChange={this.handleTabChange} className={classes.tabs}>
                        <Tab label={this.props.measureCode} value="1" />
                        <Tab label={this.props.measureCode + " by Race"} value="2" />
                        <Tab label={this.props.measureCode + " by Ethnicity"} value="3" />
                    </TabList>
                </Box>
                <TabPanel value="1">
                    {dataTbl}
                </TabPanel>
                <TabPanel value="2">
                    {dataTbl}
                </TabPanel>
                <TabPanel value="3">
                    {dataTbl}
                </TabPanel>
            </TabContext></ThemeProvider> : 
                <ThemeProvider theme={this.getMuiTheme()}>
                    <SharedMaterialReactTable
                        data={convertedData}
                        columns={this.getTableColumns(headers)}
                        title={`${this.props.measureName} (${this.props.measureCode})`}
                        isLoading={false}
                        enablePagination={true}
                        enableSorting={false}
                        enableFilterModal={false}
                        enableFiltering={false}
                        enableColumnActions={false}
                        enableRowActions={false}
                        enableGlobalSearch={false}
                        layoutMode="fixed"
                        initialState={{
                            pagination: { pageIndex: 0, pageSize: 100 },
                        }}
                        muiPaginationProps={{
                            rowsPerPageOptions: [10, 15, 20, 25, 50, 75, 100, 500, 1000],
                        }}
                        emptyMessage="No data available."
                    />
                </ThemeProvider>);
        }
    }

    private handleTabChange = (event, index: string) => {
        let data = this.state.measureData;
        switch (index) {
            case "1":
                data = this.state.measureBaseData;
                break;
            case "2":
                data = this.state.measureRaceData;
                break;
            case "3":
                data = this.state.measureEthnicityData;
                break;
            default:
                break;
        }
        this.setState({ curTabValue: index, measureData: data });
    };

    private async RefreshModel() {
        this.setState({ isLoading: true });
        const retval = await firstValueFrom(
            SubmissionsService.GetLiveMeasure(
                this.props.user,
                this.props.submissionId,
                this.props.productLine,
                this.props.measureCode,
                this.props.measurementYear
            )
        );
        if (retval instanceof AjaxError || !retval) {
            this.setRedirectState();
        } else {
            const isRaceEthnicity = (retval.measureWhole === null);
            let measureData: Array<Array<ILiveCell>> = [];
            let measureBaseData: Array<Array<ILiveCell>> = [];
            let measureRaceData: Array<Array<ILiveCell>> = [];
            let measureEthnicityData: Array<Array<ILiveCell>> = [];

            if (isRaceEthnicity) {
                measureBaseData = retval.measureBase;
                measureRaceData = retval.measureRace;
                measureEthnicityData = retval.measureEthnicity;
                measureData = retval.measureBase;
            } else {
                measureData = retval.measureWhole;
            }

            this.setState({
                measureData,
                isLoading: false,
                isRaceEthnicity,
                measureBaseData,
                measureRaceData,
                measureEthnicityData,
                curTabValue: "1"
            });
        }
    }
}

const LiveMeasureDisplayPageWithState = connect<IGenericUserProps>(genericMapStateToProps)(LiveMeasureViewer);

const LiveMeasureDisplayPage = withStyles(styles)(LiveMeasureDisplayPageWithState);

export default LiveMeasureDisplayPage;

